/**
 * /superadmin — Command Center.
 *
 * Five-second answer to: is the app healthy, are users growing, is revenue
 * coming in, are there problems right now, what needs attention today.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Area,
  AreaChart, ComposedChart, Legend,
} from "recharts";
import { ChevronRight, AlertTriangle, RefreshCw, Sparkles } from "lucide-react";
import { apiClient } from "../../services/api";
import { SectionHeader } from "./SuperadminApp";

const cardStyle = {
  background: "var(--bg-surface, #E4E2DE)",
  border: "1px solid var(--border, rgba(12,12,11,0.09))",
  borderRadius: 16,
  padding: 20,
};

export default function SuperadminCommandCenter() {
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(null);
  const [pulseAt, setPulseAt] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [glance, setGlance] = useState(null);
  const [performers, setPerformers] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [includeInternal, setIncludeInternal] = useState(false);

  const loadPulse = useCallback(() => {
    apiClient.get("/superadmin/dashboard/pulse", { params: { include_internal: includeInternal } }).then((r) => {
      setPulse(r.data);
      setPulseAt(Date.now());
    }).catch(() => {});
  }, [includeInternal]);

  const loadAlerts = useCallback(() => {
    apiClient.get("/superadmin/dashboard/alerts").then((r) => setAlerts(r.data?.alerts || [])).catch(() => {});
  }, []);

  const loadRevenue = useCallback(() => {
    apiClient.get("/superadmin/dashboard/revenue-chart?days=30").then((r) => setRevenue(r.data?.days || [])).catch(() => {});
  }, []);

  const loadGrowth = useCallback(() => {
    apiClient.get("/superadmin/dashboard/growth-chart", { params: { days: 30, include_internal: includeInternal } })
      .then((r) => setGrowth(r.data?.days || [])).catch(() => {});
  }, [includeInternal]);

  const loadGlance = useCallback(() => {
    apiClient.get("/superadmin/dashboard/today-glance").then((r) => setGlance(r.data)).catch(() => {});
  }, []);

  const loadPerformers = useCallback(() => {
    apiClient.get("/superadmin/dashboard/top-performers").then((r) => setPerformers(r.data)).catch(() => {});
  }, []);

  const loadFunnel = useCallback(() => {
    apiClient.get("/superadmin/funnel", { params: { include_internal: includeInternal } })
      .then((r) => setFunnel(r.data)).catch(() => {});
  }, [includeInternal]);

  useEffect(() => {
    loadPulse();
    loadAlerts();
    loadRevenue();
    loadGrowth();
    loadGlance();
    loadPerformers();
    loadFunnel();
    const tick = setInterval(loadPulse, 30_000);
    const tick2 = setInterval(loadAlerts, 60_000);
    return () => {
      clearInterval(tick);
      clearInterval(tick2);
    };
  }, [loadPulse, loadAlerts, loadRevenue, loadGrowth, loadGlance, loadPerformers, loadFunnel]);

  return (
    <div data-testid="command-center" className="space-y-6">
      {/* Section A — pulse tiles */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <SectionHeader title="Pulse" lastUpdated={pulseAt} onRefresh={loadPulse} />
          <button
            onClick={() => setIncludeInternal((v) => !v)}
            data-testid="include-internal-toggle"
            aria-pressed={includeInternal}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 100,
              fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              background: includeInternal ? "#0C0C0B" : "transparent",
              color: includeInternal ? "#F5C518" : "var(--text-tertiary, #8A8884)",
              border: `1px solid ${includeInternal ? "#0C0C0B" : "var(--border, rgba(12,12,11,0.15))"}`,
              cursor: "pointer",
            }}
            title="When ON, test/demo/seed/team accounts are included in DAU/funnel/retention."
          >
            {includeInternal ? "Internal ON" : "Real users only"}
          </button>
        </div>
        {pulse?.tracking_started_at && (
          <div
            data-testid="tracking-since"
            style={{
              fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase",
              color: "var(--text-tertiary, #8A8884)", fontWeight: 700, marginBottom: 8,
            }}
          >
            Activity tracking since {new Date(pulse.tracking_started_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }} data-testid="pulse-tiles">
          {pulse ? (
            <>
              <PulseTile label="Online now (5m)" value={pulse.tiles.online_now.value} delta={pulse.tiles.online_now.delta} spark={pulse.tiles.online_now.spark} testid="tile-online" />
              <PulseTile label="Active last 15m" value={pulse.tiles.active_15m?.value ?? 0} testid="tile-active-15m" />
              <PulseTile label="Active last 60m" value={pulse.tiles.active_60m?.value ?? 0} testid="tile-active-60m" />
              <PulseTile label="Revenue today" value={`₹${(pulse.tiles.revenue_today.value || 0).toLocaleString("en-IN")}`} delta={pulse.tiles.revenue_today.delta} spark={pulse.tiles.revenue_today.spark} testid="tile-revenue" />
              <PulseTile label="Workouts today" value={pulse.tiles.workouts_today.value} delta={pulse.tiles.workouts_today.delta} spark={pulse.tiles.workouts_today.spark} testid="tile-workouts" />
              <PulseTile label="New signups" value={pulse.tiles.signups_today.value} delta={pulse.tiles.signups_today.delta} spark={pulse.tiles.signups_today.spark} testid="tile-signups" />
            </>
          ) : (
            <>
              {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonTile key={i} />)}
            </>
          )}
        </div>
      </div>

      {/* Section A2 — Activation funnel */}
      <div data-testid="funnel-section" style={cardStyle}>
        <SectionHeader title="Activation funnel" />
        {funnel?.steps ? (
          <div className="space-y-2" data-testid="funnel-steps">
            {funnel.steps.map((s, i) => {
              const top = funnel.steps[0].count || 1;
              const widthPct = Math.max(3, Math.round((s.count / top) * 100));
              return (
                <div key={s.id} data-testid={`funnel-step-${s.id}`} className="flex items-center gap-3">
                  <div style={{ width: 150, fontSize: 13, color: "var(--text-primary, #0C0C0B)" }}>{s.label}</div>
                  <div style={{ flex: 1, height: 28, background: "var(--bg-inset, #ECEAE7)", borderRadius: 6, position: "relative", overflow: "hidden" }}>
                    <div style={{
                      position: "absolute", top: 0, bottom: 0, left: 0,
                      width: `${widthPct}%`,
                      background: i === 0 ? "#0C0C0B" : "#00B0D5",
                      transition: "width 300ms",
                    }} />
                    <div style={{
                      position: "absolute", inset: 0, display: "flex", alignItems: "center",
                      paddingLeft: 10, fontSize: 12, fontWeight: 700,
                      color: widthPct > 30 ? "white" : "var(--text-primary, #0C0C0B)",
                    }}>
                      {s.count.toLocaleString("en-IN")} · {s.pct_of_signup ?? 0}%
                    </div>
                  </div>
                  <div style={{ width: 90, fontSize: 12, color: "var(--text-tertiary, #8A8884)", textAlign: "right" }}>
                    {s.dropoff_from_prev_pct == null ? "—" : `${s.dropoff_from_prev_pct}% drop`}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-6 text-center" style={{ color: "var(--text-tertiary, #8A8884)", fontSize: 13 }}>Loading funnel…</div>
        )}
      </div>

      {/* Section B — alerts */}
      <div data-testid="alerts-section" style={cardStyle}>
        <SectionHeader title="Alerts requiring action" />
        {alerts.length === 0 ? (
          <div className="py-8 text-center" style={{ color: "var(--text-tertiary, #8A8884)" }}>
            <AlertTriangle size={28} style={{ margin: "0 auto", color: "var(--text-tertiary, #8A8884)" }} />
            <div className="font-barlow-cond mt-2" style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary, #0C0C0B)" }}>
              No alerts — all clear.
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => a.deeplink && navigate(a.deeplink)}
                data-testid={`alert-${a.id}`}
                className="w-full text-left flex items-center gap-3"
                style={{
                  padding: "12px 14px",
                  borderLeft: `3px solid ${a.severity === "critical" ? "var(--error, #EF4444)" : a.severity === "warning" ? "var(--warning, #F59E0B)" : "var(--accent, #2A2825)"}`,
                  background:
                    a.severity === "critical"
                      ? "rgba(239,68,68,0.08)"
                      : a.severity === "warning"
                      ? "rgba(245,158,11,0.08)"
                      : "var(--bg-inset, #D8D6D1)",
                  borderRadius: 10,
                }}
              >
                <SeverityBadge severity={a.severity} />
                <div className="flex-1 text-[14px]" style={{ color: "var(--text-primary, #0C0C0B)" }}>{a.title}</div>
                <ChevronRight size={16} style={{ color: "var(--text-tertiary, #8A8884)" }} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Section C — revenue chart */}
      <div style={cardStyle} data-testid="revenue-chart">
        <SectionHeader title="Revenue · last 30 days" onRefresh={loadRevenue} />
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={revenue} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="rgba(12,12,11,0.06)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "var(--text-tertiary, #8A8884)", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fill: "var(--text-tertiary, #8A8884)", fontSize: 11 }} />
            <Tooltip wrapperStyle={{ fontFamily: "Barlow", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontFamily: "Barlow", fontSize: 12 }} />
            <Bar dataKey="sessions" fill="var(--accent, #2A2825)" name="Sessions" />
            <Bar dataKey="marketplace" fill="var(--accent-mid, #6E6C68)" name="Marketplace" />
            <Line type="monotone" dataKey="total" stroke="var(--brand-gold, #F5C518)" strokeWidth={2.5} dot={false} name="Total" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Section D — growth + retention */}
      <div style={cardStyle} data-testid="growth-chart">
        <SectionHeader title="Growth and Retention · last 30 days" onRefresh={loadGrowth} />
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={growth} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="rgba(12,12,11,0.06)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "var(--text-tertiary, #8A8884)", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis yAxisId="left" tick={{ fill: "var(--text-tertiary, #8A8884)", fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: "var(--text-tertiary, #8A8884)", fontSize: 11 }} domain={[0, 100]} />
            <Tooltip wrapperStyle={{ fontFamily: "Barlow", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontFamily: "Barlow", fontSize: 12 }} />
            <Line yAxisId="left" type="monotone" dataKey="cumulative_registered" stroke="var(--accent, #2A2825)" strokeWidth={2.2} dot={false} name="Total registered" />
            <Line yAxisId="left" type="monotone" dataKey="dau" stroke="var(--accent-mid, #6E6C68)" strokeWidth={2} dot={false} name="DAU" />
            <Line yAxisId="right" type="monotone" dataKey="d7_retention_pct" stroke="var(--success, #22C55E)" strokeWidth={2} dot={false} name="D7 retention %" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Section E — today at a glance */}
      <div style={cardStyle} data-testid="today-glance">
        <SectionHeader title="Today at a glance" onRefresh={loadGlance} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <div className="space-y-3">
            <Glance label="Food scans" value={glance?.food_scans} />
            <Glance label="AI Trainer sessions" value={glance?.ai_trainer_sessions} />
            <Glance label="Community posts" value={glance?.community_posts} />
            <Glance label="Trainer enquiries" value={glance?.trainer_enquiries} />
          </div>
          <div className="space-y-3">
            <Glance label="Marketplace listings" value={glance?.marketplace_listings} />
            <Glance label="Marketplace orders" value={glance?.marketplace_orders} />
            <Glance label="Professional applications" value={glance?.professional_applications} />
            <Glance label="Messages" value={glance?.messages} />
          </div>
        </div>
      </div>

      {/* Section F — top performers */}
      <div data-testid="top-performers">
        <SectionHeader title="Top performers today" onRefresh={loadPerformers} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          <PerformersCard title="Most active users" rows={performers?.most_active_users || []} />
          <PerformersCard
            title="Top selling products"
            rows={(performers?.top_products || []).map((p) => ({
              name: p.title,
              avatar_url: p.photo,
              metric: p.units,
              metric_label: "units",
            }))}
          />
          <PerformersCard title="Most active trainers" rows={performers?.most_active_trainers || []} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function PulseTile({ label, value, delta, spark, testid }) {
  const sparkData = (spark || []).map((p) => ({ x: p.hour, y: p.count }));
  return (
    <div style={cardStyle} data-testid={testid}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div
            style={{
              fontFamily: "Barlow",
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--text-tertiary, #8A8884)",
            }}
          >
            {label}
          </div>
          <div
            style={{
              fontFamily: "Barlow Condensed, sans-serif",
              fontSize: 48,
              fontWeight: 900,
              lineHeight: 1,
              marginTop: 6,
              color: "var(--text-primary, #0C0C0B)",
            }}
          >
            {value ?? "—"}
          </div>
          {delta != null && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                marginTop: 6,
                color: delta >= 0 ? "var(--success, #22C55E)" : "var(--error, #EF4444)",
              }}
            >
              {delta >= 0 ? "+" : ""}
              {delta} vs yesterday
            </div>
          )}
        </div>
        <div style={{ width: 80, height: 40, minWidth: 80, minHeight: 40 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData}>
              <Area type="monotone" dataKey="y" stroke="var(--accent, #2A2825)" fill="rgba(42,40,37,0.08)" strokeWidth={1.5} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function SkeletonTile() {
  return (
    <div
      style={{
        ...cardStyle,
        height: 130,
        background: "var(--bg-inset, #D8D6D1)",
        animation: "pulse-tile 1s ease-in-out infinite alternate",
      }}
    >
      <style>{`@keyframes pulse-tile { from { opacity: 0.5 } to { opacity: 0.9 } }`}</style>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const palette =
    severity === "critical"
      ? { bg: "rgba(239,68,68,0.12)", fg: "var(--error, #EF4444)", label: "Critical" }
      : severity === "warning"
      ? { bg: "rgba(245,158,11,0.12)", fg: "var(--warning, #F59E0B)", label: "Warning" }
      : { bg: "rgba(42,40,37,0.10)", fg: "var(--text-secondary, #4A4845)", label: "Info" };
  return (
    <span
      style={{
        background: palette.bg,
        color: palette.fg,
        padding: "3px 10px",
        borderRadius: 100,
        fontFamily: "Barlow",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
      }}
    >
      {palette.label}
    </span>
  );
}

function Glance({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <span style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)" }}>{label}</span>
      <span style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: 24, fontWeight: 800, color: "var(--text-primary, #0C0C0B)" }}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function PerformersCard({ title, rows }) {
  return (
    <div style={cardStyle}>
      <div
        style={{
          fontFamily: "Barlow",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          color: "var(--text-tertiary, #8A8884)",
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      {(!rows || rows.length === 0) && (
        <div style={{ color: "var(--text-tertiary, #8A8884)", fontSize: 13, padding: "16px 0" }}>
          Nothing yet today.
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {(rows || []).map((r, i) => (
          <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < rows.length - 1 ? "1px solid var(--border, rgba(12,12,11,0.09))" : "none" }}>
            <div
              style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "var(--bg-inset, #D8D6D1)",
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--text-secondary, #4A4845)",
              }}
            >
              {r.avatar_url ? (
                <img src={r.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                (r.name || "?").charAt(0).toUpperCase()
              )}
            </div>
            <span style={{ flex: 1, fontSize: 13, color: "var(--text-primary, #0C0C0B)" }}>{r.name}</span>
            <span style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: 16, fontWeight: 800 }}>
              {r.metric}
              <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 400, color: "var(--text-tertiary, #8A8884)" }}>
                {r.metric_label}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
